
import React, { useContext, useState } from 'react';
import { AppContext } from '../App';
import { Client } from '../types';

const Briefings: React.FC = () => {
  const context = useContext(AppContext);
  const [selectedBriefing, setSelectedBriefing] = useState<Client | null>(null);

  if (!context) return null;
  const { clients, searchTerm } = context;

  const clientsWithBriefing = clients.filter(c => 
    (c.briefing && c.briefing.length > 0) &&
    (c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-4 md:p-8 pb-32 md:pb-12 max-w-7xl mx-auto flex flex-col gap-8 animate-in fade-in duration-500 overflow-x-hidden">
      <header>
        <h2 className="text-3xl md:text-4xl font-black text-text-main tracking-tight">Central de Briefings</h2>
        <p className="text-text-secondary font-bold text-base md:text-lg mt-1">Analise as demandas enviadas via Onboarding.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-4 flex flex-col gap-4">
          <p className="text-[10px] font-black text-text-secondary uppercase tracking-[0.3em] ml-1">Briefings Recebidos</p>
          <div className="flex flex-col gap-3 max-h-[70vh] overflow-y-auto pr-2 scrollbar-hide">
            {clientsWithBriefing.length > 0 ? (
              clientsWithBriefing.map(client => (
                <button 
                  key={client.id}
                  onClick={() => setSelectedBriefing(client)}
                  className={`flex items-center gap-4 p-5 rounded-[23px] border-2 transition-all text-left group ${
                    selectedBriefing?.id === client.id 
                      ? 'bg-primary/5 border-primary shadow-lg shadow-primary/5' 
                      : 'bg-white border-gray-100 hover:border-gray-200'
                  }`}
                >
                  <img src={client.logoUrl} className="size-12 rounded-xl object-cover bg-gray-50 shadow-sm" alt="" />
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-black truncate transition-colors ${selectedBriefing?.id === client.id ? 'text-primary' : 'text-text-main'}`}>
                      {client.name}
                    </p>
                    <p className="text-[9px] font-bold text-text-secondary uppercase mt-0.5 tracking-wider">{client.id}</p>
                  </div>
                  <span className="material-symbols-outlined text-primary/40 group-hover:translate-x-1 transition-transform">chevron_right</span>
                </button>
              ))
            ) : (
              <div className="bg-white p-10 rounded-[23px] border border-gray-100 text-center">
                <span className="material-symbols-outlined text-gray-200 text-4xl mb-3">inbox_customize</span>
                <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Nenhum briefing preenchido ainda.</p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-8">
          {selectedBriefing ? (
            <div className="bg-white rounded-[23px] border border-gray-100 shadow-sm overflow-hidden animate-in slide-in-from-right-4 duration-500">
              <div className="p-8 md:p-10 bg-primary text-white flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-5 text-center sm:text-left">
                  <img src={selectedBriefing.logoUrl} className="size-20 rounded-2xl bg-white p-2 object-contain" alt="" />
                  <div>
                    <h3 className="text-2xl font-black tracking-tight">{selectedBriefing.name}</h3>
                    <p className="text-white/60 text-[10px] font-black uppercase tracking-[0.2em] mt-1">Briefing de Projeto • {selectedBriefing.id}</p>
                  </div>
                </div>
                <div className="flex flex-col items-center sm:items-end gap-2">
                   <span className="px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-[10px] font-black uppercase tracking-widest">Status: Completo</span>
                   <p className="text-white/40 text-[9px] font-bold uppercase tracking-widest">Enviado por {selectedBriefing.contactName}</p>
                </div>
              </div>

              <div className="p-8 md:p-12 flex flex-col gap-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {selectedBriefing.briefing?.map((field) => (
                    <div key={field.id} className="group flex flex-col gap-4">
                      <div className="p-6 rounded-[23px] bg-background-light border-2 border-transparent group-hover:border-primary/5 group-hover:bg-white transition-all shadow-sm">
                        <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em] mb-2">{field.label}</p>
                        <p className="text-sm font-bold text-text-main leading-relaxed">
                          {field.value || <span className="text-text-secondary opacity-30 italic">Informação não fornecida</span>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-6">
                   <div className="flex items-center gap-4">
                      <img src={selectedBriefing.avatarUrl} className="size-12 rounded-full border-4 border-gray-50" alt="" />
                      <div>
                        <p className="text-xs font-black text-text-main">Contato de Referência</p>
                        <p className="text-[10px] font-bold text-text-secondary">{selectedBriefing.email}</p>
                      </div>
                   </div>
                   <button 
                     onClick={() => {
                        const summary = selectedBriefing.briefing?.map(f => `${f.label}: ${f.value}`).join('\n\n');
                        navigator.clipboard.writeText(summary || '');
                        alert('Resumo copiado para a área de transferência!');
                     }}
                     className="h-14 px-8 rounded-2xl bg-gray-50 text-text-main hover:bg-primary-light hover:text-primary transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-3"
                   >
                     <span className="material-symbols-outlined text-[20px]">content_copy</span>
                     Copiar Resumo
                   </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-[23px] border-2 border-dashed border-gray-100 h-96 flex flex-col items-center justify-center text-center p-12">
               <div className="size-24 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                 <span className="material-symbols-outlined text-5xl text-gray-200">manage_search</span>
               </div>
               <h3 className="text-xl font-black text-text-main tracking-tight">Selecione um cliente</h3>
               <p className="text-sm font-bold text-text-secondary mt-2 max-w-xs">Escolha um registro na lista ao lado para visualizar os detalhes do briefing enviado.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Briefings;
